/**
 * Header Component
 *
 * Top header bar with user menu and plan indicator.
 *
 * @module components/layout/Header
 */

import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks';
import { Avatar, Button } from '@/components/ui';
import {
  Menu,
  User,
  Settings,
  LogOut,
  ChevronDown,
  TrendingUp,
  Building2,
  Check,
  Shield,
} from 'lucide-react';
import {
  type PlanId,
  PLAN_TIERS,
  formatRequestCount,
} from '@hummingbird/pricing';

// =============================================================================
// Admin Link (Super Admins Only)
// =============================================================================

function AdminLink() {
  const { isSuperAdmin } = useAuth();

  if (!isSuperAdmin) return null;

  return (
    <Link
      to="/admin"
      className={cn(
        'flex items-center gap-2 px-3 py-2 rounded-lg transition-all',
        'text-sm font-medium text-brand-600 hover:text-brand-700',
        'bg-brand-50 hover:bg-brand-100 border border-brand-200'
      )}
    >
      <Shield className="h-4 w-4" />
      <span className="hidden sm:inline">Admin</span>
    </Link>
  );
}

// =============================================================================
// User Menu
// =============================================================================

function UserMenu() {
  const { user, organization, switchOrganization, signOut } = useAuth();
  const { hasAnySettingsAccess } = usePermissions();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const orgMemberships = user?.orgMemberships || [];
  const hasMultipleOrgs = orgMemberships.length > 1;

  // Get display name with firstName/lastName support
  const displayName = (() => {
    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
    return fullName || user?.displayName || null;
  })();

  // Close menu on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    setOpen(false);
    await signOut();
    navigate('/login', { state: { loggedOut: true } });
  };

  const handleSwitchOrg = async (orgId: string) => {
    if (orgId === organization?.id || switching) return;

    setSwitching(true);
    try {
      await switchOrganization(orgId);
      setOpen(false);
      // Navigate to dashboard to refresh with new org context
      navigate('/overview');
    } catch (error) {
      console.error('Failed to switch organization:', error);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'flex items-center gap-2 p-1.5 rounded-lg transition-colors',
          'hover:bg-gray-100',
          open && 'bg-gray-100'
        )}
      >
        <Avatar
          src={user?.photoUrl}
          name={displayName || user?.email || 'User'}
          size="sm"
        />
        <div className="hidden sm:block text-left">
          <span className="text-sm font-medium text-gray-700 block max-w-[120px] truncate">
            {displayName || user?.email?.split('@')[0] || 'User'}
          </span>
          {organization && (
            <span className="text-xs text-gray-500 block max-w-[120px] truncate">
              {organization.name}
            </span>
          )}
        </div>
        <ChevronDown className={cn(
          'h-4 w-4 text-gray-400 transition-transform',
          open && 'rotate-180'
        )} />
      </button>

      {/* Dropdown Menu */}
      {open && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl border border-gray-200 shadow-medium py-1 z-50 animate-fade-in">
          {/* User Info */}
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900 truncate">
              {displayName || 'User'}
            </p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            {organization && (
              <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                <Building2 className="h-3.5 w-3.5" />
                <span className="truncate">{organization.name}</span>
              </div>
            )}
          </div>

          {/* Organization Switcher (only show if multiple orgs) */}
          {hasMultipleOrgs && (
            <div className="py-1 border-b border-gray-100">
              <div className="px-4 py-1.5">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Switch Organization
                </p>
              </div>
              {orgMemberships.map((membership) => (
                <button
                  key={membership.orgId}
                  onClick={() => handleSwitchOrg(membership.orgId)}
                  disabled={switching}
                  className={cn(
                    'flex items-center justify-between w-full px-4 py-2 text-sm hover:bg-gray-50',
                    membership.orgId === organization?.id
                      ? 'text-brand-600 font-medium'
                      : 'text-gray-700'
                  )}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Building2 className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{membership.orgName}</span>
                  </div>
                  {membership.orgId === organization?.id && (
                    <Check className="h-4 w-4 flex-shrink-0 text-brand-600" />
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Menu Items */}
          <div className="py-1">
            <Link
              to="/profile"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <User className="h-4 w-4" />
              Profile
            </Link>
            {hasAnySettingsAccess && (
              <Link
                to="/settings"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Settings className="h-4 w-4" />
                Settings
              </Link>
            )}
          </div>

          {/* Sign Out */}
          <div className="border-t border-gray-100 py-1">
            <button
              onClick={handleSignOut}
              className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Plan Usage Indicator
// =============================================================================

function PlanUsageIndicator() {
  const navigate = useNavigate();
  const { organization } = useAuth();
  const { canAccessBilling } = usePermissions();

  // Don't show usage indicator for users without billing access
  if (!canAccessBilling) {
    return null;
  }

  const planId = (organization?.planId || 'free') as PlanId;
  const plan = PLAN_TIERS[planId];
  const isFree = planId === 'free';

  // Usage calculation
  const bucketMax = organization?.currentBucketMax;
  const usageThisPeriod = organization?.usageThisPeriod || 0;
  const lifetimeUsage = organization?.lifetimeUsage || 0;
  const usage = isFree ? lifetimeUsage : usageThisPeriod;
  const usageLimit = isFree ? 2000 : (bucketMax ?? plan.maxRequests);
  const usagePercent = Math.min((usage / usageLimit) * 100, 100);
  const isNearLimit = usagePercent >= 80;
  const isAtLimit = usagePercent >= 100;

  return (
    <button
      onClick={() => navigate('/settings/billing')}
      className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-lg transition-all border',
        'hover:border-gray-300 hover:shadow-sm',
        'border-gray-200 bg-white'
      )}
    >
      {/* Usage info with inline progress */}
      <div className="flex items-center gap-3">
        {/* Plan badge */}
        <span
          className={cn(
            'text-xs font-semibold px-2 py-0.5 rounded-md',
            isFree ? 'bg-gray-100 text-gray-600' : 'bg-brand-50 text-brand-700'
          )}
        >
          {plan.name}
        </span>

        {/* Usage text and bar */}
        <div className="hidden sm:flex items-center gap-2">
          <span
            className={cn(
              'text-xs font-medium tabular-nums',
              isAtLimit && 'text-error-600',
              isNearLimit && !isAtLimit && 'text-warning-600',
              !isNearLimit && 'text-gray-600'
            )}
          >
            {formatRequestCount(usage)}/{formatRequestCount(usageLimit)}
          </span>

          {/* Mini circular progress or bar */}
          <div className="w-12 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all',
                isAtLimit && 'bg-error-500',
                isNearLimit && !isAtLimit && 'bg-warning-500',
                !isNearLimit && 'bg-brand-500'
              )}
              style={{ width: `${usagePercent}%` }}
            />
          </div>

          {isNearLimit && (
            <TrendingUp
              className={cn(
                'h-3.5 w-3.5',
                isAtLimit ? 'text-error-500' : 'text-warning-500'
              )}
            />
          )}
        </div>
      </div>

      {/* Upgrade CTA for free users */}
      {isFree && (
        <span className="text-xs font-semibold text-brand-600 hover:text-brand-700">
          Upgrade
        </span>
      )}
    </button>
  );
}

// =============================================================================
// Header Component
// =============================================================================

interface HeaderProps {
  onMenuClick?: () => void;
  className?: string;
}

export function Header({ onMenuClick, className }: HeaderProps) {
  const { canAccessBilling } = usePermissions();
  const { isSuperAdmin } = useAuth();

  // Show divider if there's something to the left of user menu (usage indicator or admin link)
  const showDivider = canAccessBilling || isSuperAdmin;

  return (
    <header
      className={cn(
        'flex items-center justify-between h-16 px-4 bg-white border-b border-gray-200',
        className
      )}
    >
      {/* Left side */}
      <div className="flex items-center gap-4">
        {/* Mobile menu button */}
        <Button
          variant="ghost"
          size="icon-sm"
          className="lg:hidden"
          onClick={onMenuClick}
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" />
        </Button>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-2">
        {/* Admin Link (Super Admins Only) */}
        <AdminLink />

        {/* Plan Usage Indicator */}
        <PlanUsageIndicator />

        {/* Divider - only show if there's something to the left */}
        {showDivider && <div className="w-px h-6 bg-gray-200 mx-2" />}

        {/* User Menu */}
        <UserMenu />
      </div>
    </header>
  );
}
